import React, { useState, useEffect, useCallback } from "react";

import {
  Users,
  Activity,
  Home,
  MessageCircle,
  Users2,
  FileText,
  Music2,
  ClipboardCheck,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { useTheme } from "../../context/themeContext";
import { useAuth } from "../../context/authContext";
import { BRAND, TOKENS, CardShell } from "./dashboardShared";
import serverUrl from "../../utils/utilsser";

function PulseStyles() {
  return (
    <style>{`
      @keyframes ta-plus-one-pop {
        0%   { opacity: 0; transform: translate(-50%, 4px) scale(0.7); }
        15%  { opacity: 1; transform: translate(-50%, -4px) scale(1.1); }
        30%  { transform: translate(-50%, -8px) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -26px) scale(1); }
      }
      .ta-plus-one-badge {
        position: absolute;
        top: -10px;
        left: 50%;
        font-size: 11px;
        font-weight: 800;
        color: #fff;
        background: linear-gradient(90deg, #ef5da8, #8b7cf6);
        padding: 2px 8px;
        border-radius: 9999px;
        box-shadow: 0 4px 12px rgba(139,124,246,0.5);
        animation: ta-plus-one-pop 1.6s ease-out forwards;
        pointer-events: none;
        z-index: 30;
        white-space: nowrap;
      }
    `}</style>
  );
}

function TimerStatCard({
  icon: Icon,
  tint,
  title,
  users,
  totalTime,
  avgTime,
  loading,
  pulse,
  pulseKey,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  return (
    <div
      className={
        "relative flex flex-col gap-3.5 rounded-2xl border px-4 py-5 transition-all duration-300 hover:-translate-y-0.5  " +
        t.card +
        " " +
        t.cardShadow
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 20px 45px -20px ${tint}66`;
        e.currentTarget.style.borderColor = `${tint}55`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "";
      }}
    >
      {pulse && (
        <span key={pulseKey} className="ta-plus-one-badge">
          +1
        </span>
      )}

      <div className="flex items-center gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${tint}1f`, color: tint }}
        >
          <Icon size={19} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1 truncate text-sm font-semibold ">
          <span className={t.textPrimary}>{title}</span>
        </div>
      </div>

      <div
        className="grid grid-cols-3 gap-2 rounded-xl border px-2.5 py-2.5"
        style={{ borderColor: `${tint}30`, backgroundColor: `${tint}0d` }}
      >
        <div className="flex flex-col items-center text-center">
          <span
            className={
              "text-[10px] font-medium uppercase tracking-wide " + t.textFaint
            }
          >
            Users
          </span>
          <span
            className={
              "mt-0.5 text-base font-bold tabular-nums " + t.textPrimary
            }
          >
            {loading ? "…" : (users ?? "--")}
          </span>
        </div>

        <div
          className="flex flex-col items-center border-x text-center"
          style={{ borderColor: `${tint}30` }}
        >
          <span
            className={
              "text-[10px] font-medium uppercase tracking-wide " + t.textFaint
            }
          >
            Total Time
          </span>
          <span
            className={
              "mt-0.5 text-base font-bold tabular-nums " + t.textPrimary
            }
          >
            {loading ? "…" : totalTime != null ? `${totalTime}m` : "--"}
          </span>
        </div>

        <div className="flex flex-col items-center text-center">
          <span
            className={
              "text-[10px] font-medium uppercase tracking-wide " + t.textFaint
            }
          >
            Avg Time
          </span>
          <span
            className={
              "mt-0.5 text-base font-bold tabular-nums " + t.textPrimary
            }
          >
            {loading ? "…" : avgTime != null ? `${avgTime}m` : "--"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section — stat grid + Patients Overview chart
// ---------------------------------------------------------------------------

function PageTimingSection({ therapistData, loading, error, pulse, pulseKey }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  // API shape: { analytics: [ { page, uniqueUsers, totalTime, averageTime }, ... ] }
  const analytics = therapistData?.analytics ?? [];
  console.log(analytics);

  const getPage = (pageName) =>
    analytics.find((a) => a.page?.toLowerCase() === pageName.toLowerCase());

  const stats = [
    { key: "home", icon: Home, tint: "#5b8def", title: "Home", page: "home" },
    {
      key: "chatbot",
      icon: MessageCircle,
      tint: "#34d9b8",
      title: "Healo Chat",
      page: "healo",
    },
    {
      key: "worksheets",
      icon: FileText,
      tint: "#8b7cf6",
      title: "WorkSheets",
      page: "worksheets",
    },
    {
      key: "music",
      icon: Music2,
      tint: "#ec4899",
      title: "Music",
      page: "music",
    },
  ];

  return (
    <div className="mb-10">
      <PulseStyles />
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div
            className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
            style={{ color: BRAND.pink }}
          >
            <Activity size={14} />
            Overview
          </div>
          <h1
            className={
              "text-4xl font-extrabold tracking-tight " + t.textPrimary
            }
          >
            OverAll Overview
          </h1>
        </div>
      </div>

      {error ? (
        <CardShell accent="pink">Unable to load timing data.</CardShell>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:col-span-2 lg:grid-cols-1 xl:grid-cols-2">
            {stats.map((s) => {
              const row = getPage(s.page);
              return (
                <TimerStatCard
                  key={s.key}
                  icon={s.icon}
                  tint={s.tint}
                  title={s.title}
                  users={row?.uniqueUsers}
                  totalTime={row?.totalTime}
                  avgTime={row?.averageTime}
                  loading={loading}
                  pulse={s.key === "counselling" && pulse}
                  pulseKey={pulseKey}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OverallAnalytics({
  therapistData: therapistDataProp,
  loading: loadingProp,
  error: errorProp,
  wrapInPageShell = true,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { code } = useAuth();

  const isControlled = therapistDataProp !== undefined;

  const [therapistData, setTherapistData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);

  const triggerPulse = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setPulseKey((k) => k + 1);
    setPulse(true);
    setTimeout(() => setPulse(false), 1600);
  }, []);

  // NEW — live event, mirrors ta-test-notification handling in TestAnalyticsSection
  useEffect(() => {
    const handler = () => triggerPulse();
    window.addEventListener("ta-therapist-notification", handler);
    return () =>
      window.removeEventListener("ta-therapist-notification", handler);
  }, [triggerPulse]);

  // NEW — replay a pending notification stored before navigation
  useEffect(() => {
    const raw = sessionStorage.getItem("pendingTherapistNotification");
    if (!raw) return;
    sessionStorage.removeItem("pendingTherapistNotification");
    triggerPulse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isControlled) return; // parent is supplying data, skip own fetch

    const loadPageOverview = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `https://hr-api.infiheal.com/api/v1/users/get-page-timer/${code}`,
        );

        if (!res.ok) {
          throw new Error("Failed to fetch therapist overview");
        }

        const json = await res.json();
        setTherapistData(json.data);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPageOverview();
  }, [code, isControlled, refreshKey]);

  const section = (
    <PageTimingSection
      therapistData={isControlled ? therapistDataProp : therapistData}
      loading={isControlled ? loadingProp : loading}
      error={isControlled ? errorProp : error}
      pulse={pulse} // NEW
      pulseKey={pulseKey}
    />
  );

  if (!wrapInPageShell) return section;

  return (
    <div className={"min-h-screen w-full " + t.pageWash}>
      <div className={"w-full px-4 py-8 sm:px-8 lg:px-12 " + t.textPrimary}>
        <div className="mx-auto max-w-6xl">{section}</div>
      </div>
    </div>
  );
}

// Exported for reuse/testing if a consuming page wants the raw section
// without the fetch wrapper or page shell.
